import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { PageCard } from '../layout/page/PageCard';
import { FlagIcon, PlayIcon, QueueListIcon } from '@heroicons/react/24/outline';
import { DeleteIconButton } from '../ui/DeleteIconButton';
import { DuplicateIconButton } from '../ui/DuplicateIconButton';
import { EditIconButton } from '../ui/EditIconButton';
import { IconActionButton } from '../ui/IconActionButton';
import { InlineAlert } from '../ui/InlineAlert';
import {
  assignPlanningWorkItem,
  createPlanningWorkItem,
  deletePlanningWorkItem,
  duplicatePlanningWorkItem,
  getWorkItemDeleteImpact,
  listPlanningObjectives,
  listPlanningWorkItems,
  PlanningCycle,
  PlanningDeleteImpact,
  PlanningObjective,
  PlanningWorkItem,
  PlanningWorkItemUpsertPayload,
  startPlanningWorkItemExecution,
  updatePlanningWorkItem,
} from '../../services/strategicPlanningApi';
import { fetchMyTasksScope } from '../../services/myTasksApi';
import { canEditPlanningTaskOwner, formatPlanningTaskPriority } from '../../types/PlanningTaskStatus';
import { CollaboratorAutocomplete } from '../ui/CollaboratorAutocomplete';
import { StrategicPlanningTabId } from '../../pages/strategic-planning/StrategicPlanningTabId';
import type { CollaboratorOption } from '../ui/CollaboratorAutocomplete';
import { PlanningDeleteConfirmModal } from './PlanningDeleteConfirmModal';
import { PlanningDuplicateModal } from './PlanningDuplicateModal';
import { PlanningFiltersBar } from './PlanningFiltersBar';
import { PlanningStatusBadge } from './PlanningStatusBadge';
import { PlanningWorkItemFormModal } from './PlanningWorkItemFormModal';
import { formatDateBr } from '../../utils/formatDateBr';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { sortPlanningObjectives, sortPlanningWorkItems } from '../../utils/planningListSort';
import { nextSortOrder } from '../../utils/sortOrderList';

type PlanningWorkItemsListViewProps = {
  kind: 'Meta' | 'Marco';
  canWrite: boolean;
  cycles: PlanningCycle[];
  selectedCycleFilter: string;
  selectedObjectiveFilter: string;
  selectedMetaFilter: string;
  createModalOpen: boolean;
  onCreateModalOpenChange: (open: boolean) => void;
  onCycleFilterChange: (value: string) => void;
  onObjectiveFilterChange: (value: string) => void;
  onMetaFilterChange: (value: string) => void;
  onNavigateToTab: (
    tab: StrategicPlanningTabId,
    filters?: { cycleId?: string; objectiveId?: string; metaId?: string; marcoId?: string },
  ) => void;
};

export function PlanningWorkItemsListView({
  kind,
  canWrite,
  cycles,
  selectedCycleFilter,
  selectedObjectiveFilter,
  selectedMetaFilter,
  createModalOpen,
  onCreateModalOpenChange,
  onCycleFilterChange,
  onObjectiveFilterChange,
  onMetaFilterChange,
  onNavigateToTab,
}: PlanningWorkItemsListViewProps) {
  const [objectives, setObjectives] = useState<PlanningObjective[]>([]);
  const [metas, setMetas] = useState<PlanningWorkItem[]>([]);
  const [items, setItems] = useState<PlanningWorkItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [collaboratorOptions, setCollaboratorOptions] = useState<CollaboratorOption[]>([]);
  const [workItemModal, setWorkItemModal] = useState<{
    initial?: PlanningWorkItem | null;
    objectiveId?: string;
    parentWorkItemId?: string;
    defaultCycleId?: string;
    defaultPlannedStartDate?: string;
    defaultPlannedEndDate?: string;
  } | null>(null);
  const [deleteContext, setDeleteContext] = useState<{
    item: PlanningWorkItem;
    impact: PlanningDeleteImpact;
    transferOptions: Array<{ id: string; label: string }>;
  } | null>(null);
  const [duplicateContext, setDuplicateContext] = useState<{
    item: PlanningWorkItem;
    counts: { marcos?: number; tasks?: number };
  } | null>(null);
  const [startingId, setStartingId] = useState<string | null>(null);
  const [assigningId, setAssigningId] = useState<string | null>(null);

  const kindLabel = kind === 'Meta' ? 'meta' : 'marco';
  const kindLabelPlural = kind === 'Meta' ? 'metas' : 'marcos';
  const kindParticiple = kind === 'Marco' ? 'o' : 'a';

  const resolveDefaultPlannedDates = useCallback((): { start?: string; end?: string } => {
    const cycle = cycles.find((item) => item.id === selectedCycleFilter);

    if (kind === 'Marco' && selectedMetaFilter) {
      const parentMeta = metas.find((meta) => meta.id === selectedMetaFilter);
      if (parentMeta?.plannedStartDate && parentMeta?.plannedEndDate) {
        return { start: parentMeta.plannedStartDate, end: parentMeta.plannedEndDate };
      }
    }

    if (selectedObjectiveFilter) {
      const objective = objectives.find((item) => item.id === selectedObjectiveFilter);
      if (objective?.plannedStartDate && objective?.plannedEndDate) {
        return { start: objective.plannedStartDate, end: objective.plannedEndDate };
      }
    }

    if (cycle) {
      return { start: cycle.startDate, end: cycle.endDate };
    }

    return {};
  }, [
    cycles,
    kind,
    metas,
    objectives,
    selectedCycleFilter,
    selectedMetaFilter,
    selectedObjectiveFilter,
  ]);

  const objectiveOptions = useMemo(
    () =>
      sortPlanningObjectives(
        objectives.filter((objective) => !selectedCycleFilter || objective.cycleId === selectedCycleFilter),
        cycles,
      ).map((objective) => ({ id: objective.id, label: objective.title })),
    [cycles, objectives, selectedCycleFilter],
  );

  const metaOptions = useMemo(
    () =>
      sortPlanningWorkItems(
        metas.filter((meta) => !selectedObjectiveFilter || meta.objectiveId === selectedObjectiveFilter),
        { cycles, objectives, parents: [] },
      ).map((meta) => ({ id: meta.id, label: meta.title })),
    [cycles, metas, objectives, selectedObjectiveFilter],
  );

  const filteredItems = useMemo(() => {
    let result = items;

    if (selectedCycleFilter) {
      result = result.filter((item) => item.cycleId === selectedCycleFilter);
    }

    if (selectedObjectiveFilter) {
      result = result.filter((item) => item.objectiveId === selectedObjectiveFilter);
    }

    if (kind === 'Marco' && selectedMetaFilter) {
      result = result.filter((item) => item.parentWorkItemId === selectedMetaFilter);
    }

    if (kind === 'Meta') {
      result = result.filter((item) => !item.parentWorkItemId);
    }

    return result;
  }, [items, kind, selectedCycleFilter, selectedMetaFilter, selectedObjectiveFilter]);

  const loadData = useCallback(async (): Promise<void> => {
    // Uma chamada de objetivos + work-items em paralelo; filtros de objetivo/meta no cliente.
    if (kind === 'Meta') {
      const [allObjectives, workItems] = await Promise.all([
        listPlanningObjectives(),
        listPlanningWorkItems({ kind: 'Meta' }),
      ]);
      const metaItems = workItems.filter((item) => !item.parentWorkItemId);
      setObjectives(allObjectives);
      setMetas(metaItems);
      setItems(workItems);
      return;
    }

    const [allObjectives, metaItems, workItems] = await Promise.all([
      listPlanningObjectives(),
      listPlanningWorkItems({ kind: 'Meta' }),
      listPlanningWorkItems({ kind: 'Marco' }),
    ]);

    setObjectives(allObjectives);
    setMetas(metaItems.filter((item) => !item.parentWorkItemId));
    setItems(workItems);
  }, [kind]);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      await loadData();
    } catch {
      toast.error(`Falha ao carregar ${kindLabelPlural}.`);
    } finally {
      setLoading(false);
    }
  }, [kindLabelPlural, loadData]);

  const sortedItems = useMemo(
    () =>
      sortPlanningWorkItems(filteredItems, {
        cycles,
        objectives,
        parents: kind === 'Marco' ? metas : [],
      }),
    [filteredItems, kind, metas, objectives, cycles],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    void fetchMyTasksScope()
      .then((scope) => {
        if (cancelled) return;
        setCollaboratorOptions(
          scope.defaultUsers.map((user) => ({
            userId: user.userId,
            name: user.name,
            email: user.email,
            isSelf: user.isSelf,
            isSubordinate: user.isSubordinate,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setCollaboratorOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleAssign(item: PlanningWorkItem, ownerUserId: string): Promise<void> {
    setAssigningId(item.id);
    try {
      const updated = await assignPlanningWorkItem(item.id, { ownerUserId });
      setItems((current) => current.map((row) => (row.id === updated.id ? updated : row)));
      if (kind === 'Meta') {
        setMetas((current) => current.map((row) => (row.id === updated.id ? updated : row)));
      }
    } catch (error) {
      toast.error(getRequestErrorMessage(error, `Falha ao atribuir responsável d${kind === 'Marco' ? 'o' : 'a'} ${kindLabel}.`));
      throw error instanceof Error ? error : new Error('work_item_assign_failed');
    } finally {
      setAssigningId(null);
    }
  }

  async function handleStart(item: PlanningWorkItem): Promise<void> {
    if (!item.ownerUserId) {
      toast.info(
        kind === 'Marco'
          ? 'Atribua um responsável ao marco antes de iniciar.'
          : 'Atribua um responsável à meta antes de iniciar.',
      );
      return;
    }

    setStartingId(item.id);
    try {
      await startPlanningWorkItemExecution(item.id, { executorUserId: item.ownerUserId });
      toast.success(`${kindLabel[0].toUpperCase()}${kindLabel.slice(1)} iniciad${kindParticiple}.`);
      await refresh();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, `Falha ao iniciar ${kindLabel}.`));
    } finally {
      setStartingId(null);
    }
  }

  useEffect(() => {
    if (createModalOpen) {
      const defaultDates = resolveDefaultPlannedDates();
      setWorkItemModal({
        objectiveId: selectedObjectiveFilter || undefined,
        parentWorkItemId: kind === 'Marco' ? selectedMetaFilter || undefined : undefined,
        defaultCycleId: selectedCycleFilter || undefined,
        defaultPlannedStartDate: defaultDates.start,
        defaultPlannedEndDate: defaultDates.end,
      });
      onCreateModalOpenChange(false);
    }
  }, [
    createModalOpen,
    kind,
    onCreateModalOpenChange,
    resolveDefaultPlannedDates,
    selectedCycleFilter,
    selectedMetaFilter,
    selectedObjectiveFilter,
  ]);

  async function handleWorkItemSubmit(payload: PlanningWorkItemUpsertPayload): Promise<void> {
    try {
      if (workItemModal?.initial) {
        await updatePlanningWorkItem(workItemModal.initial.id, payload);
        toast.success(`${kind} atualizad${kindParticiple}.`);
      } else {
        await createPlanningWorkItem(payload);
        toast.success(`${kind} criad${kindParticiple}.`);
      }
      setWorkItemModal(null);
      await refresh();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar item.'));
      throw new Error('work_item_save_failed');
    }
  }

  async function openDelete(item: PlanningWorkItem): Promise<void> {
    try {
      const impact = await getWorkItemDeleteImpact(item.id);
      let transferOptions: Array<{ id: string; label: string }> = [];

      if (kind === 'Meta') {
        transferOptions = metas
          .filter((meta) => meta.id !== item.id)
          .map((meta) => ({ id: meta.id, label: meta.title }));
      } else {
        const siblingMarcos = items.filter((marco) => marco.parentWorkItemId === item.parentWorkItemId);
        transferOptions = siblingMarcos
          .filter((marco) => marco.id !== item.id)
          .map((marco) => ({ id: marco.id, label: marco.title }));

        const metaOptionsTransfer = metas
          .filter((meta) => meta.id !== item.parentWorkItemId)
          .map((meta) => ({ id: meta.id, label: `Meta: ${meta.title}` }));
        transferOptions.push(...metaOptionsTransfer);
      }

      setDeleteContext({ item, impact, transferOptions });
    } catch {
      toast.error('Não foi possível avaliar o impacto da exclusão.');
    }
  }

  async function openDuplicate(item: PlanningWorkItem): Promise<void> {
    try {
      const children = await listPlanningWorkItems({ parentWorkItemId: item.id });
      setDuplicateContext({
        item,
        counts: {
          marcos: kind === 'Meta' ? children.filter((child) => child.kind === 'Marco').length : undefined,
          tasks: children.filter((child) => child.kind === 'Tarefa').length,
        },
      });
    } catch {
      toast.error('Não foi possível preparar a duplicação.');
    }
  }

  function resolveObjectiveTitle(objectiveId?: string | null): string {
    return objectives.find((objective) => objective.id === objectiveId)?.title ?? '—';
  }

  function resolveMetaTitle(metaId?: string | null): string {
    return metas.find((meta) => meta.id === metaId)?.title ?? '—';
  }

  return (
    <div className="space-y-4">
      <PlanningFiltersBar
        cycles={cycles}
        selectedCycleFilter={selectedCycleFilter}
        onCycleFilterChange={onCycleFilterChange}
        showObjectiveFilter
        selectedObjectiveFilter={selectedObjectiveFilter}
        onObjectiveFilterChange={onObjectiveFilterChange}
        objectiveOptions={objectiveOptions}
        showMetaFilter={kind === 'Marco'}
        selectedMetaFilter={selectedMetaFilter}
        onMetaFilterChange={onMetaFilterChange}
        metaOptions={metaOptions}
        counterLabel={`${sortedItems.length} ${kindLabel}(s)`}
      />

      <PageCard title={kind === 'Meta' ? 'Metas' : 'Marcos'}>
        {loading ? (
          <p className="text-sm text-secondary-600">Carregando…</p>
        ) : sortedItems.length === 0 ? (
          <InlineAlert>Nenhum(a) {kindLabel} cadastrado(a). Selecione o item pai nos filtros e cadastre o primeiro.</InlineAlert>
        ) : (
          <div className="overflow-x-auto">
            <table className="erp-table w-full min-w-[900px]">
              <thead>
                <tr>
                  <th>Título</th>
                  {kind === 'Meta' ? <th>Objetivo</th> : <th>Meta</th>}
                  {kind === 'Meta' ? <th>Prioridade</th> : null}
                  <th>Status</th>
                  <th>Responsável</th>
                  <th>Período</th>
                  <th className="w-56">{canWrite ? 'Ações' : ''}</th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="font-medium text-secondary-900">{item.title}</div>
                        {item.description ? (
                          <div
                            className="max-w-xs truncate text-xs text-secondary-500"
                            title={item.description}
                          >
                            {item.description}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        {kind === 'Meta' ? (
                          <div
                            className="max-w-xs truncate"
                            title={resolveObjectiveTitle(item.objectiveId)}
                          >
                            {resolveObjectiveTitle(item.objectiveId)}
                          </div>
                        ) : (
                          <div
                            className="max-w-xs truncate"
                            title={resolveMetaTitle(item.parentWorkItemId)}
                          >
                            {resolveMetaTitle(item.parentWorkItemId)}
                          </div>
                        )}
                      </td>
                      {kind === 'Meta' ? (
                        <td>{formatPlanningTaskPriority(item.priority)}</td>
                      ) : null}
                      <td>
                        <PlanningStatusBadge scope="workItem" status={item.status} />
                      </td>
                      <td className="min-w-[220px]">
                        {canWrite && canEditPlanningTaskOwner(item.status, item.executionFinishedAt) ? (
                          <CollaboratorAutocomplete
                            valueUserId={item.ownerUserId}
                            valueUserName={item.ownerUserName}
                            disabled={assigningId === item.id}
                            saving={assigningId === item.id}
                            optionsSource={collaboratorOptions}
                            onCommit={(user) => {
                              if (!user) return;
                              void handleAssign(item, user.userId);
                            }}
                          />
                        ) : (
                          <span className="text-sm text-secondary-800">
                            {item.ownerUserName?.trim() || 'Não atribuído'}
                          </span>
                        )}
                      </td>
                      <td>
                        {formatDateBr(item.plannedStartDate)} — {formatDateBr(item.plannedEndDate)}
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          {kind === 'Meta' ? (
                            <IconActionButton
                              label="Ver marcos"
                              icon={FlagIcon}
                              onClick={() =>
                                onNavigateToTab('marcos', {
                                  cycleId: item.cycleId ?? undefined,
                                  objectiveId: item.objectiveId ?? undefined,
                                  metaId: item.id,
                                })
                              }
                            />
                          ) : (
                            <IconActionButton
                              label="Ver tarefas"
                              icon={QueueListIcon}
                              onClick={() =>
                                onNavigateToTab('tasks', {
                                  cycleId: item.cycleId ?? undefined,
                                  objectiveId: item.objectiveId ?? undefined,
                                  metaId: item.parentWorkItemId ?? undefined,
                                  marcoId: item.id,
                                })
                              }
                            />
                          )}
                          {canWrite ? (
                            <>
                              {(item.status === 'Planned' || item.status === 'Overdue')
                                && !item.executionFinishedAt ? (
                                <button
                                  type="button"
                                  className="erp-btn-primary erp-btn-sm gap-1"
                                  disabled={startingId === item.id}
                                  title={`Iniciar ${kindLabel}`}
                                  onClick={() => void handleStart(item)}
                                >
                                  <PlayIcon className="h-4 w-4" />
                                  Iniciar
                                </button>
                              ) : null}
                              <DuplicateIconButton
                                label={`Duplicar ${kindLabel}`}
                                onClick={() => void openDuplicate(item)}
                              />
                              <EditIconButton
                                label={`Editar ${kindLabel}`}
                                onClick={() =>
                                  setWorkItemModal({
                                    initial: item,
                                    objectiveId: item.objectiveId ?? undefined,
                                    parentWorkItemId: item.parentWorkItemId ?? undefined,
                                    defaultCycleId: item.cycleId ?? undefined,
                                  })
                                }
                              />
                              <DeleteIconButton
                                label={`Excluir ${kindLabel}`}
                                onClick={() => void openDelete(item)}
                              />
                            </>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageCard>

      {workItemModal ? (
        <PlanningWorkItemFormModal
          open
          kind={kind}
          cycles={cycles}
          objectives={objectives}
          parentOptions={kind === 'Marco' ? metas : undefined}
          objectiveId={workItemModal.objectiveId}
          parentWorkItemId={workItemModal.parentWorkItemId}
          defaultCycleId={workItemModal.defaultCycleId}
          defaultPlannedStartDate={workItemModal.defaultPlannedStartDate}
          defaultPlannedEndDate={workItemModal.defaultPlannedEndDate}
          defaultSortOrder={nextSortOrder(
            items.filter((item) => {
              const objectiveId = workItemModal.initial?.objectiveId ?? workItemModal.objectiveId;
              const parentWorkItemId =
                workItemModal.initial?.parentWorkItemId ?? workItemModal.parentWorkItemId;

              if (kind === 'Meta') {
                return !item.parentWorkItemId && item.objectiveId === objectiveId;
              }

              return item.parentWorkItemId === parentWorkItemId;
            }),
          )}
          initial={workItemModal.initial}
          onClose={() => setWorkItemModal(null)}
          onSubmit={handleWorkItemSubmit}
        />
      ) : null}

      {deleteContext ? (
        <PlanningDeleteConfirmModal
          open
          title={`Excluir ${kindLabel}`}
          itemLabel={deleteContext.item.title}
          impact={deleteContext.impact}
          transferOptions={deleteContext.transferOptions}
          transferLabel={kind === 'Meta' ? 'Mover filhos para a meta' : 'Mover tarefas para o marco ou meta'}
          onClose={() => setDeleteContext(null)}
          onConfirm={async (transferChildrenToId) => {
            await deletePlanningWorkItem(deleteContext.item.id, { transferChildrenToId });
            toast.success(`${kind} excluíd${kindParticiple}.`);
            setDeleteContext(null);
            await refresh();
          }}
        />
      ) : null}

      {duplicateContext ? (
        <PlanningDuplicateModal
          open
          entityType={kind === 'Meta' ? 'meta' : 'marco'}
          itemLabel={duplicateContext.item.title}
          counts={duplicateContext.counts}
          onClose={() => setDuplicateContext(null)}
          onConfirm={async (options) => {
            await duplicatePlanningWorkItem(duplicateContext.item.id, {
              includeMarcos: options.includeMarcos,
              includeTasks: options.includeTasks,
            });
            toast.success(`${kind} duplicad${kindParticiple}.`);
            setDuplicateContext(null);
            await refresh();
          }}
        />
      ) : null}
    </div>
  );
}
